/* eslint-disable no-unused-vars */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { saveApplication, saveChatResponse } from '../../../services/questionService';
import { getSummaryGenerator } from '../../../services/getSummary';
import LoadingScreen from './loadingScreen';
import TermsAndConditions from './termsAndConditions';
import {
  ContentBox,
  QuestionBox,
  TextInput,
  ContinueButton,
  PropertyList,
  PropertyItem,
  PropertyLabel,
  PropertyValue,
  Main,
  SectionLeft,
  SectionRight,
  ContentBoxRight,
  ActionLink,
  Wrapper,
  ProgressContainer,
  ProgressBarWrapper,
  ProgressBarFill,
  ProgressInfo,
  ProgressText,
  RadioGroup,
  RadioOption,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalText,
  ModalButton,
} from './generateQuestion.style';
import { ErrorMessageComponent } from '../../../components/uielements/error';
import {
  cacheAnswer,
  cacheSummary,
  clearCache,
  clearSummaryCache,
  getCache,
  getCacheOverlaysScreenshots,
  getSummaryCache,
} from '../../../helper/cacheHelper';
import { clearItem, getItem, setItem } from '../../../helper/localStorageHelper';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { IntlMessages } from '../../../helper/langHelper';
import { FormattedMessage, useIntl } from 'react-intl';
import MapWithOverlays from '../mapView/mapWithOverlays';
import {
  getCriterias,
  validateAnswer,
  askQuestions,
  getQuestions,
} from '../../../services/assessment';

// Constants for phase management
const PHASES = {
  INIT: 'INIT',
  ASK_QUESTIONS: 'ASK_QUESTIONS',
  GET_QUESTIONS: 'GET_QUESTIONS',
  TERMS: 'TERMS',
  COMPLETED: 'COMPLETED',
  NOT_SUPPORTED: 'NOT_SUPPORTED',
};

const DEV_TYPES = {
  AD: 'AD',
  ARAD: 'ARAD',
  ASD: 'ASD',
};

const DEV_TYPE_LABELS = {
  AD: 'Accepted Development',
  ARAD: 'Accepted Development Subject To Requirements',
  ASD: 'Assessable Development',
};

const QuestionComponent = () => {
  const [answer, setAnswer] = useState('');
  const [proposal, setProposal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [loadingQuestionId, setLoadingQuestionId] = useState(null);
  const [error, setError] = useState('');
  const [isEndQuestion, setIsEndQuestion] = useState(false);
  const [isProcedureCompleted, setIsProcedureCompleted] = useState(false);
  const [showNotSupportedModal, setShowNotSupportedModal] = useState(false);

  // Assessment flow states
  const [assessmentId, setAssessmentId] = useState(null);
  const [currentDevType, setCurrentDevType] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [completionRate, setCompletionRate] = useState(0);
  const [responseList, setResponseList] = useState([]);
  const [allAnswers, setAllAnswers] = useState([]);
  const [currentPhase, setCurrentPhase] = useState(PHASES.INIT);

  // Critical flow control
  const [criteriaData, setCriteriaData] = useState(null);

  // getQuestions flow states
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [allQuestions, setAllQuestions] = useState([]);
  const [currentQuestionData, setCurrentQuestionData] = useState(null);

  // NEW: Current RAD name for display
  const [currentRadName, setCurrentRadName] = useState('');
  const cacheOverlayScreenshots = getCacheOverlaysScreenshots();

  const location = useLocation();
  const navigate = useNavigate();
  const selectedProcedure = location.state?.selectedProcedure || '';
  const propertyAddress = location.state?.searchAddress?.address || '';
  const overlays = location.state?.overlays || '';
  const fullName = location.state?.fullName || '';
  const email = location.state?.email || '';
  const phone = location.state?.phone || '';
  const description = location.state?.inputValue || '';
  const screenshotIdFromUrl = location.state?.screenshotId || '';
  const zoneDetails = location.state?.zoneDetailsData || {};
  const applicationIdForConvertToFinal = location.state?.applicationId || null;

  const procedurePermit = getItem('procedurePermit') || {};
  const qaCoversation = getCache();
  const summarayCache = getSummaryCache();
  const screenshotId = getItem('screenshotId');
  const intl = useIntl();

  // Initialize component
  useEffect(() => {
    const isTerminateExists = qaCoversation.some(
      (entry) => entry?.question === 'TERMINATE' && entry?.answer === 'TERMINATE'
    );

    if (!isTerminateExists) {
      clearCache();
      clearSummaryCache();
    }

    fetchInitialData();
  }, []);

  // Error handler utility
  const handleError = useCallback((errorMessage, fallbackKey = 'generic.error') => {
    setError(<IntlMessages id={fallbackKey} defaultMessage={errorMessage} />);
    setLoading(false);
  }, []);

  // Reset form state
  const resetFormState = useCallback(() => {
    setAnswer('');
    setError('');
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);

    const isTerminateExists = qaCoversation.some(
      (entry) => entry?.question === 'TERMINATE' && entry?.answer === 'TERMINATE'
    );

    if (isTerminateExists) {
      setIsProcedureCompleted(true);
      setLoading(false);
      return;
    }

    await fetchCriteriaFlow();
    setLoading(false);
  };

  const fetchCriteriaFlow = async () => {
    try {
      const criteriaPayload = {
        regionId: procedurePermit?.results[0]?.regionId,
        precinctId: procedurePermit?.results[0]?.precinctId,
        procedureId: procedurePermit?.results[0]?.procedureId,
      };

      const criteriaResponse = await getCriterias(criteriaPayload);
      setCriteriaData(criteriaResponse);

      const { assessmentId, acceptedDevCount, acceptedReqDevCount, assessableDevCount } =
        criteriaResponse;
      setAssessmentId(assessmentId);

      // Priority order: AD -> ARAD -> ASD
      // Check for valid (non-null AND > 0) dev types
      const hasValidAD = acceptedDevCount !== null;
      const hasValidARAD = acceptedReqDevCount !== null;
      const hasValidASD = assessableDevCount !== null;

      if (hasValidAD) {
        // Start with AD - no getQuestions needed
        setCurrentDevType(DEV_TYPES.AD);
        setCurrentPhase(PHASES.ASK_QUESTIONS);
        await fetchNextQuestion(assessmentId, DEV_TYPES.AD, []);
      } else if (hasValidARAD) {
        // Skip AD (null or invalid), start with ARAD - needs getQuestions
        setCurrentDevType(DEV_TYPES.ARAD);
        setCurrentPhase(PHASES.ASK_QUESTIONS);
        await fetchNextQuestion(assessmentId, DEV_TYPES.ARAD, []);
      } else if (hasValidASD) {
        // Skip AD and ARAD (null or invalid), start with ASD - needs getQuestions
        setCurrentDevType(DEV_TYPES.ASD);
        setCurrentPhase(PHASES.ASK_QUESTIONS);
        await fetchNextQuestion(assessmentId, DEV_TYPES.ASD, []);
      } else {
        // All dev types are null or invalid - show not supported
        setShowNotSupportedModal(true);
        setCurrentPhase(PHASES.NOT_SUPPORTED);
      }
    } catch (err) {
      console.error('Error fetching criteria:', err);
      handleError('Error fetching criteria. Please try again.', 'criteria.fetch.error');
    }
  };

  const fetchNextQuestion = async (assessmentIdParam, devType, responseListParam) => {
    setLoading(true);
    try {
      const payload = {
        assessmentId: assessmentIdParam,
        responseList: responseListParam,
        devType: devType,
      };

      const response = await askQuestions(payload);

      setCompletionRate(response.completionRate);
      setCurrentQuestion(response.nextQuestion);

      // CRITICAL FIX: Check BOTH conditions with AND logic
      // completionRate === 100
      if (response.completionRate === 100) {
        // askQuestions completed for this dev type
        await handleAskQuestionsComplete(devType);
      } else {
        resetFormState();
      }
    } catch (err) {
      console.error('Error fetching next question:', err);
      handleError('Error fetching question. Please try again.', 'question.fetch.error');
    } finally {
      setLoading(false);
    }
  };

  const handleAskQuestionsComplete = async (devType) => {
    // Check which dev type just completed
    if (devType === DEV_TYPES.AD) {
      // AD completed successfully - go directly to Terms & Conditions
      // Do NOT call getQuestions
      setCurrentPhase(PHASES.TERMS);
      setIsEndQuestion(true);
    } else if (devType === DEV_TYPES.ARAD || devType === DEV_TYPES.ASD) {
      // ARAD or ASD completed - call getQuestions
      await fetchQuestionsPhase(devType);
    } else {
      // Fallback - go to terms
      setCurrentPhase(PHASES.TERMS);
      setIsEndQuestion(true);
    }
  };

  const fetchQuestionsPhase = async (devType) => {
    setLoading(true);

    try {
      const questionsPayload = {
        regionId: procedurePermit?.results[0]?.regionId,
        precinctId: procedurePermit?.results[0]?.precinctId,
        procedureId: procedurePermit?.results[0]?.procedureId,
        devType: devType,
      };

      const questionsResponse = await getQuestions(questionsPayload);

      // Flatten all questions
      const flattenedQuestions = [];
      if (questionsResponse.packs && questionsResponse.packs.length > 0) {
        questionsResponse.packs.forEach((pack, packIndex) => {
          if (pack.radIdList && pack.radIdList.length > 0) {
            pack.radIdList.forEach((rad, radIndex) => {
              if (rad.questionArray && rad.questionArray.length > 0) {
                rad.questionArray.forEach((question, qIndex) => {
                  flattenedQuestions.push({
                    ...question,
                    packIndex,
                    radIndex,
                    radName: rad.radName,
                    radId: rad.radId || `RAD${radIndex + 1}`,
                    devType: devType,
                    devTypeName: DEV_TYPE_LABELS[devType],
                    originalIndex: qIndex,
                    uniqueId: `${devType}-${packIndex}-${radIndex}-${qIndex}`,
                  });
                });
              }
            });
          }
        });
      }

      if (flattenedQuestions.length > 0) {
        setAllQuestions(flattenedQuestions);
        setCurrentQuestionData(flattenedQuestions[0]);
        setCurrentQuestionIndex(0);
        setCurrentRadName(flattenedQuestions[0].radName || flattenedQuestions[0].radId);
        setCurrentPhase(PHASES.GET_QUESTIONS);
      } else {
        // No questions, proceed to terms
        setCurrentPhase(PHASES.TERMS);
        setIsEndQuestion(true);
      }
    } catch (err) {
      console.error('Error fetching questions:', err);
      handleError('Error fetching questions. Please try again.', 'questions.fetch.error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError('');

    if (!answer || (typeof answer === 'string' && answer.trim() === '')) {
      setError(<IntlMessages id="answer.required" defaultMessage="Please provide an answer." />);
      return;
    }

    setLoading(true);

    try {
      if (currentPhase === PHASES.ASK_QUESTIONS) {
        await handleAskQuestionsSubmit();
      } else if (currentPhase === PHASES.GET_QUESTIONS) {
        await handleGetQuestionsSubmit();
      }
    } catch (err) {
      console.error('Error submitting answer:', err);
      handleError('Error submitting answer. Please try again.', 'submit.error');
    } finally {
      setLoading(false);
    }
  };

  const handleAskQuestionsSubmit = async () => {
    // Step 1: Validate the answer
    const validatePayload = {
      assessmentId: assessmentId,
      devType: currentDevType,
      question: currentQuestion,
      responseData: answer,
    };

    const validationResponse = await validateAnswer(validatePayload);
    const responseScore = validationResponse?.data?.responseScore || 0;

    // Step 2: Generate summary with responseScore and responseTo
    const summaryText = await generateSummaryForAnswer(
      currentQuestion,
      answer,
      responseScore,
      currentDevType
    );

    // Step 3: Cache answer
    cacheAnswer(currentQuestion, answer);

    const answerData = {
      question: currentQuestion,
      answer: answer,
      devType: currentDevType,
      validationScore: responseScore,
      passed: responseScore >= 90,
      timestamp: new Date().toISOString(),
    };
    setAllAnswers((prev) => [answerData, ...prev]);

    // Step 4: Check validation score
    if (responseScore < 90) {
      // Failed validation - move to next dev type immediately (one strike rule)
      await handleValidationFailure();
    } else {
      // Passed validation - continue with current dev type
      const newResponseList = [...responseList, summaryText];
      setResponseList(newResponseList);
      await fetchNextQuestion(assessmentId, currentDevType, newResponseList);
    }
  };

  const handleValidationFailure = async () => {
    const { acceptedDevCount, acceptedReqDevCount, assessableDevCount } = criteriaData;

    if (currentDevType === DEV_TYPES.AD) {
      // AD failed, move to ARAD if available
      if (acceptedReqDevCount !== null) {
        setCurrentDevType(DEV_TYPES.ARAD);
        setResponseList([]);
        setCompletionRate(0);
        resetFormState();
        await fetchNextQuestion(assessmentId, DEV_TYPES.ARAD, []);
      } else if (assessableDevCount !== null) {
        // Move to ASD if ARAD not available
        setCurrentDevType(DEV_TYPES.ASD);
        setResponseList([]);
        setCompletionRate(0);
        resetFormState();
        await fetchNextQuestion(assessmentId, DEV_TYPES.ASD, []);
      } else {
        // No more dev types available
        setShowNotSupportedModal(true);
        setCurrentPhase(PHASES.NOT_SUPPORTED);
      }
    } else if (currentDevType === DEV_TYPES.ARAD) {
      // ARAD failed, move to ASD if available
      if (assessableDevCount !== null) {
        setCurrentDevType(DEV_TYPES.ASD);
        setResponseList([]);
        setCompletionRate(0);
        resetFormState();
        await fetchNextQuestion(assessmentId, DEV_TYPES.ASD, []);
      } else {
        // No more dev types available
        setShowNotSupportedModal(true);
        setCurrentPhase(PHASES.NOT_SUPPORTED);
      }
    } else if (currentDevType === DEV_TYPES.ASD) {
      // ASD failed - not supported (no more dev types)
      setShowNotSupportedModal(true);
      setCurrentPhase(PHASES.NOT_SUPPORTED);
    }
  };

  const handleGetQuestionsSubmit = async () => {
    // Save current answer
    const answerData = {
      question: currentQuestionData?.point || `Question ${currentQuestionIndex + 1}`,
      answer: answer,
      questionIndex: currentQuestionIndex,
      phase: currentPhase,
      devType: currentQuestionData?.devType,
      radName: currentQuestionData?.radName,
      radId: currentQuestionData?.radId,
      uniqueId: currentQuestionData?.uniqueId,
      timestamp: new Date().toISOString(),
    };

    setAllAnswers((prev) => [answerData, ...prev]);
    cacheAnswer(currentQuestionData?.point || `Question ${currentQuestionIndex + 1}`, answer);

    // Generate summary with responseTo (RAD ID)
    const responseTo =
      currentQuestionData?.radId || currentQuestionData?.radName || currentQuestionData?.devType;
    await generateSummaryForGetQuestions(currentQuestionData, answer, responseTo);

    // Move to next question
    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex < allQuestions.length) {
      setCurrentQuestionIndex(nextIndex);
      setCurrentQuestionData(allQuestions[nextIndex]);
      setCurrentRadName(allQuestions[nextIndex].radName || allQuestions[nextIndex].radId);
      resetFormState();
    } else {
      setCurrentPhase(PHASES.TERMS);
      setIsEndQuestion(true);
    }
  };

  const generateSummaryForAnswer = async (question, answer, responseScore, responseTo) => {
    if (!question || !answer) return '';

    setLoadingQuestionId(question);
    setSummaryLoading(true);

    const placeholderSummary = {
      question: question,
      answer: '',
      isLoading: true,
      responseScore: responseScore,
      responseTo: responseTo,
    };
    setProposal((prev) => [placeholderSummary, ...prev]);

    try {
      const payload = {
        question: question,
        responseData: answer,
      };
      const data = await getSummaryGenerator(payload);

      const summaryText = data?.output || 'Unable to generate summary.';
      const newSummary = {
        question: question,
        answer: summaryText,
        isLoading: false,
        responseScore: responseScore,
        responseTo: responseTo,
      };

      // Cache with additional metadata
      cacheSummary(question, summaryText, responseScore, responseTo);
      setProposal((prev) => prev.map((item) => (item.question === question ? newSummary : item)));

      return summaryText;
    } catch (err) {
      console.error('Error generating summary:', err);
      const errorText = 'Error generating summary.';
      const errorSummary = {
        question: question,
        answer: errorText,
        isLoading: false,
        responseScore: responseScore,
        responseTo: responseTo,
      };
      cacheSummary(question, errorText, responseScore, responseTo);
      setProposal((prev) => prev.map((item) => (item.question === question ? errorSummary : item)));
      return errorText;
    } finally {
      setSummaryLoading(false);
      setLoadingQuestionId(null);
    }
  };

  const generateSummaryForGetQuestions = async (questionData, answer, responseTo) => {
    if (!questionData?.point || !answer) return;

    const questionId = questionData.point;
    setLoadingQuestionId(questionId);
    setSummaryLoading(true);

    const placeholderSummary = {
      question: questionId,
      answer: '',
      isLoading: true,
      responseScore: null,
      responseTo: responseTo,
    };
    setProposal((prev) => [placeholderSummary, ...prev]);

    try {
      const payload = {
        question: questionData.point,
        responseData: answer,
      };
      const data = await getSummaryGenerator(payload);

      const summaryText = data?.output || 'Unable to generate summary.';
      const newSummary = {
        question: questionData.point,
        answer: summaryText,
        isLoading: false,
        responseScore: null,
        responseTo: responseTo,
      };

      // Cache with responseTo
      cacheSummary(questionData.point, summaryText, null, responseTo);
      setProposal((prev) => prev.map((item) => (item.question === questionId ? newSummary : item)));
    } catch (err) {
      const errorText = 'Error generating summary.';
      const errorSummary = {
        question: questionData.point,
        answer: errorText,
        isLoading: false,
        responseScore: null,
        responseTo: responseTo,
      };
      cacheSummary(questionData.point, errorText, null, responseTo);
      setProposal((prev) =>
        prev.map((item) => (item.question === questionId ? errorSummary : item))
      );
    } finally {
      setSummaryLoading(false);
      setLoadingQuestionId(null);
    }
  };

  const completeProcedure = async () => {
    setLoading(true);

    const applicationPayload = {
      address: propertyAddress,
      description: description,
      fullName: fullName,
      procedureId: selectedProcedure?._id,
      // overlayArray: overlays?.map((overlayName) => ({
      //   overlay: overlayName?.overlayName || overlayName?.overlay,
      // })),

      overlayArray: overlays
        ?.filter(
          (overlayName) =>
            (overlayName?.overlayName || overlayName?.overlay)?.toLowerCase() !== 'title report'
        )
        .map((overlayName) => ({
          overlay: overlayName?.overlayName || overlayName?.overlay,
        })),
      phoneNumber: phone,
      email: email,
      propertyDetails: zoneDetails,
      assessmentAnswers: allAnswers,
      assessmentId: assessmentId,
      completionTimestamp: new Date().toISOString(),
      screenshotId: screenshotId || screenshotIdFromUrl,
      recordId: cacheOverlayScreenshots?.find((item) => item?.name === true)?.screenshot,
      ...(applicationIdForConvertToFinal && { applicationId: applicationIdForConvertToFinal }),
    };

    try {
      const applicationResponse = await saveApplication(applicationPayload);
      const applicationId = applicationResponse?.applicationId;

      if (!applicationId) {
        handleError('Failed to save the application.', 'application.save.error');
        return;
      }

      setItem('applicationId', applicationId);

      const chatResponsePayload = {
        applicationId: applicationId,
        responseArray: summarayCache,
      };

      await saveChatResponse(chatResponsePayload);
      cacheAnswer('TERMINATE', 'TERMINATE');
      setIsProcedureCompleted(true);
      setCurrentPhase(PHASES.COMPLETED);
    } catch (appError) {
      console.error('Error saving application:', appError);
      handleError('Failed to save application. Please try again.', 'application.save.retry.error');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = () => {
    completeProcedure();
  };

  const handleReportView = () => {
    setIsEndQuestion(false);
    navigate('/reviewAndLodge');
  };

  const handleStartNewProcedure = () => {
    clearCache();
    clearSummaryCache();
    clearItem('applicationId');
    navigate('/');
  };

  const handleNotSupportedClose = () => {
    setShowNotSupportedModal(false);
    handleStartNewProcedure();
  };

  const calculateProgress = () => {
    if (currentPhase === PHASES.ASK_QUESTIONS) {
      return completionRate;
    } else if (currentPhase === PHASES.GET_QUESTIONS) {
      if (allQuestions.length === 0) return 0;
      return Math.round(((currentQuestionIndex + 1) / allQuestions.length) * 100);
    }
    return 0;
  };

  const renderProgressBar = () => {
    if (currentPhase !== PHASES.ASK_QUESTIONS && currentPhase !== PHASES.GET_QUESTIONS) {
      return null;
    }

    const progress = calculateProgress();
    let label = '';
    let subtitle = '';

    if (currentPhase === PHASES.ASK_QUESTIONS) {
      label = DEV_TYPE_LABELS[currentDevType];
      subtitle = `${completionRate}% Complete`;
    } else if (currentPhase === PHASES.GET_QUESTIONS) {
      // Display RAD name
      label = currentRadName || currentQuestionData?.radName || 'Assessment';
      subtitle = `${currentQuestionIndex + 1} / ${allQuestions.length}`;
    }

    return (
      <ProgressContainer>
        <ProgressInfo>
          <div>
            <ProgressText style={{ fontWeight: 'bold', fontSize: '16px' }}>{label}</ProgressText>{' '}
            {currentPhase === PHASES.GET_QUESTIONS && (
              <ProgressText style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                {currentQuestionData?.devTypeName || ''}
              </ProgressText>
            )}
          </div>
          <ProgressText>{subtitle}</ProgressText>
        </ProgressInfo>
        <ProgressBarWrapper>
          <ProgressBarFill progress={progress} />
        </ProgressBarWrapper>
      </ProgressContainer>
    );
  };

  const renderRadioOptions = () => {
    return (
      <RadioGroup>
        <RadioOption checked={answer === 'Yes'}>
          <input
            type="radio"
            name="answer"
            value="Yes"
            checked={answer === 'Yes'}
            onChange={(e) => setAnswer(e.target.value)}
            disabled={loading || summaryLoading}
          />
          <span>Yes</span>
        </RadioOption>

        <RadioOption checked={answer === 'No'}>
          <input
            type="radio"
            name="answer"
            value="No"
            checked={answer === 'No'}
            onChange={(e) => setAnswer(e.target.value)}
            disabled={loading || summaryLoading}
          />
          <span>No</span>
        </RadioOption>

        <RadioOption checked={answer === 'Not Applicable'}>
          <input
            type="radio"
            name="answer"
            value="Not Applicable"
            checked={answer === 'Not Applicable'}
            onChange={(e) => setAnswer(e.target.value)}
            disabled={loading || summaryLoading}
          />
          <span>Not Applicable</span>
        </RadioOption>
      </RadioGroup>
    );
  };

  const renderCurrentQuestion = () => {
    if (currentPhase === PHASES.ASK_QUESTIONS && !currentQuestion) return null;
    if (currentPhase === PHASES.GET_QUESTIONS && !currentQuestionData) return null;

    return (
      <>
        {renderProgressBar()}

        <QuestionBox role="heading" aria-level="1">
          {currentPhase === PHASES.ASK_QUESTIONS
            ? currentQuestion
            : currentQuestionData?.point || 'Unable to retrieve the question'}
        </QuestionBox>

        <div role="form">
          {currentPhase === PHASES.GET_QUESTIONS ? (
            renderRadioOptions()
          ) : (
            <>
              <label id="answer-label" htmlFor="answer-input" className="visually-hidden">
                <IntlMessages id="input.answer.label" />
              </label>
              <TextInput
                id="answer-input"
                type="text"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder={intl.formatMessage({ id: 'input.answer.placeholder' })}
                aria-labelledby="answer-label"
                aria-required="true"
                disabled={loading || summaryLoading}
              />
            </>
          )}
        </div>

        <ContinueButton
          onClick={handleSubmit}
          disabled={!answer || loading || summaryLoading}
          aria-disabled={!answer || loading || summaryLoading}
        >
          {loading ? (
            <IntlMessages id="button.loading" defaultMessage="Loading..." />
          ) : (
            <IntlMessages id="button.next" defaultMessage="Next" />
          )}
        </ContinueButton>
      </>
    );
  };

  return (
    <Main role="main">
      {showNotSupportedModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <AlertCircle size={32} color="#dc2626" />
              <ModalTitle>Not a Supported Development</ModalTitle>
            </ModalHeader>
            <ModalText>
              The use you&apos;ve proposed in {zoneDetails?.zoneName || 'Rural Residential'} would
              be classed as an Impact Assessable Application and it may be possible if assessed on
              its own merits and achieves the outcomes of the zone. <br />
              We would recommend you seek further advice from a town planner.
            </ModalText>
            <ModalButton onClick={handleNotSupportedClose}>Return to Home</ModalButton>
          </ModalContent>
        </ModalOverlay>
      )}

      <SectionLeft
        className="hide-scroll-bar"
        aria-label="Question Section"
        isEndQuestion={isEndQuestion}
      >
        {error ? (
          <ContentBox>
            <ErrorMessageComponent message={error} role="alert" aria-live="assertive" />
          </ContentBox>
        ) : (
          <>
            {isProcedureCompleted ? (
              <ContentBox>
                <QuestionBox role="heading" aria-level="1">
                  <PropertyLabel as="h2" style={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircle size={24} style={{ color: '#3DB654', marginRight: '8px' }} />
                    <IntlMessages id="preparation.completed.title" />
                  </PropertyLabel>
                </QuestionBox>
                <p style={{ fontSize: '14px', lineHeight: '22px' }}>
                  <FormattedMessage
                    id="preparation.completed.description"
                    values={{
                      procedureName: selectedProcedure?.procedureName,
                    }}
                  />{' '}
                  <ActionLink onClick={handleReportView} style={{ marginRight: '10px' }}>
                    <IntlMessages id="button.final.report" defaultMessage="Final Report" />
                  </ActionLink>{' '}
                  <ActionLink onClick={handleStartNewProcedure}>
                    <IntlMessages id="button.start.new" defaultMessage="Start New Procedure" />
                  </ActionLink>
                </p>
              </ContentBox>
            ) : (
              <ContentBox role="region" aria-label="Question Form" className="hide-scroll-bar">
                {loading ? (
                  <LoadingScreen />
                ) : isEndQuestion ? (
                  <TermsAndConditions handleGenerateReport={handleGenerateReport} />
                ) : (
                  renderCurrentQuestion()
                )}
              </ContentBox>
            )}
          </>
        )}

        <Wrapper>
          <ContentBoxRight>
            <section aria-label="Proposal Summary">
              <PropertyLabel as="h2">
                <IntlMessages id="proposal.title" />
              </PropertyLabel>
              <div style={{ display: 'grid', gap: '10px' }}>
                <PropertyValue style={{ fontWeight: 'bold' }}>
                  1. {selectedProcedure?.procedureName || 'Loading...'}
                </PropertyValue>
                <PropertyValue className="hide-scroll-bar">
                  <ul
                    aria-label="Proposal details"
                    style={{ marginLeft: '25px', listStyleType: 'disc' }}
                  >
                    {isProcedureCompleted ? (
                      <>
                        {summarayCache?.map((item, index) => (
                          <li key={index}>{item?.answer}</li>
                        ))}
                      </>
                    ) : proposal.length > 0 ? (
                      proposal?.map((item, index) => (
                        <li key={index}>
                          {item.isLoading ? (
                            <span role="status" aria-live="polite">
                              <IntlMessages id="proposal.status.generating" />
                            </span>
                          ) : (
                            item?.answer
                          )}
                        </li>
                      ))
                    ) : (
                      <IntlMessages id="proposal.status.pending" />
                    )}
                  </ul>
                </PropertyValue>
              </div>
            </section>
          </ContentBoxRight>

          <ContentBox aria-label="Property Details">
            <PropertyList role="list">
              <PropertyItem role="listitem">
                <PropertyLabel as="h2">
                  <IntlMessages id="property.details.address" />
                </PropertyLabel>
                <PropertyValue>{propertyAddress}</PropertyValue>
              </PropertyItem>

              <PropertyItem role="listitem">
                <PropertyLabel as="h2">
                  <IntlMessages id="property.details.size" />
                </PropertyLabel>
                <PropertyValue>
                  {`${zoneDetails.preLdzArea}m²` || <IntlMessages id="fetch.error" />}
                </PropertyValue>
              </PropertyItem>
              <PropertyItem role="listitem">
                <PropertyLabel as="h2">
                  <IntlMessages id="property.details.zone" />
                </PropertyLabel>
                <PropertyValue>
                  {zoneDetails?.zoneName || <IntlMessages id="fetch.error" />}
                </PropertyValue>
              </PropertyItem>
            </PropertyList>
          </ContentBox>
        </Wrapper>
      </SectionLeft>

      <SectionRight>
        <MapWithOverlays searchAddress={propertyAddress} showDropdown={true} />
      </SectionRight>
    </Main>
  );
};

export default QuestionComponent;
